// ADV-SIM-ACCOUNT-CONNECTION-CLARITY-001 — pure helpers that turn a supervision
// account into plain-English connection / mode / trading-allowed clarity + a glossary
// of the confusing status codes. Display-only; no order/broker path, no live-enable path.

export interface ClarityAccount {
  mode?: string | null;                 // "simulation" | "paper" | "live"
  broker?: string | null;               // e.g. "admin_paper_broker"
  no_trade_status?: string | null;      // CRITICAL / WARNING / ...
  risk_status?: string | null;          // YELLOW / RED / GREEN
  review_status?: string | null;        // CURRENT / REVIEW_OVERDUE / ...
  last_review_at?: string | null;
  last_trade_at?: string | null;
  alert_codes?: string[] | null;
}

export type ConnTone = 'green' | 'amber' | 'gray' | 'red';

function _parse(ts?: string | null): number | null {
  if (!ts) return null;
  // tolerate "2026-06-26 18:40:28" (treat space form as UTC) and ISO with Z.
  const norm = /\dZ?$|[+-]\d\d:?\d\d$/.test(ts) ? ts : ts.replace(' ', 'T') + 'Z';
  const t = Date.parse(norm);
  return Number.isFinite(t) ? t : null;
}

/** Account type label. These house accounts are simulation/paper — never live. */
export function modeLabel(account: ClarityAccount): { label: string; tone: ConnTone } {
  // The supervisor's `mode` field is authoritative; the broker string is only a fallback
  // when mode is absent (admin_paper_broker accounts run in 'simulation' mode = Simulation).
  const m = String(account.mode || '').toLowerCase();
  const broker = String(account.broker || '').toLowerCase();
  if (m === 'live') return { label: 'Live', tone: 'red' };
  if (m === 'simulation' || m === 'sim') return { label: 'Simulation', tone: 'gray' };
  if (m === 'paper') return { label: 'Paper', tone: 'gray' };
  if (broker.includes('paper')) return { label: 'Paper', tone: 'gray' };
  return { label: m ? m.toUpperCase() : 'Unknown', tone: 'gray' };
}

/** Data-connection freshness. These rows are ledger-backed supervision snapshots, NOT a
 *  live broker session — 'Connected' here means the supervision data is fresh, not that a
 *  live broker is wired. */
export function connectionStatus(account: ClarityAccount, nowMs?: number): {
  label: 'Connected' | 'Cached' | 'Unknown'; tone: ConnTone; detail: string;
} {
  const now = nowMs ?? Date.now();
  const reviewed = _parse(account.last_review_at);
  const detailBase = 'Ledger-backed house simulation account (supervision snapshot) — not a live broker session.';
  if (reviewed === null) return { label: 'Unknown', tone: 'gray', detail: detailBase + ' No verification timestamp.' };
  const ageH = (now - reviewed) / 3_600_000;
  if (ageH <= 2) return { label: 'Connected', tone: 'green', detail: detailBase + ' Supervision data is fresh.' };
  return { label: 'Cached', tone: 'amber', detail: detailBase + ' Showing the last supervised snapshot.' };
}

/** These accounts are never allowed to trade live (paper/simulation + safe mode). */
export function tradingAllowed(account: ClarityAccount): { allowed: false; reason: string } {
  const codes = (account.alert_codes || []).map((c) => String(c).toUpperCase());
  const m = modeLabel(account).label;
  let reason = `${m} account — live trading blocked (safe mode). Adversyn never places live orders.`;
  if (codes.includes('STALE_ACCOUNT_CRITICAL') || codes.includes('STALE_ACCOUNT_EMERGENCY')) {
    reason += ' Also flagged idle/stale.';
  }
  if (codes.includes('UNDEPLOYED_CAPITAL')) reason += ' Buying power is undeployed (idle).';
  if (codes.includes('RISK_REVIEW_REQUIRED')) reason += ' Daily-loss risk review required.';
  return { allowed: false, reason };
}

// Plain-English meanings for the confusing status codes. Used as chip/badge tooltips.
export const STATUS_GLOSSARY: Record<string, string> = {
  CRITICAL: 'CRITICAL (no-trade status): the account is idle past the critical threshold (72+ market hours). This is an ATTENTION / staleness flag, not a financial emergency — risk stays advisory.',
  CURRENT: 'CURRENT (review status): the supervisor reviewed this account within the review window; its status is up to date.',
  YELLOW: 'RISK YELLOW: advisory caution (idle / undeployed / review overdue). RED is reserved for a true loss breach or account failure — never mere inactivity.',
  RISK_YELLOW: 'RISK YELLOW: advisory caution (idle / undeployed / review overdue). RED is reserved for a true loss breach or account failure — never mere inactivity.',
  UNDEPLOYED_CAPITAL: 'UNDEPLOYED CAPITAL: more than 50% of equity is sitting as un-invested buying power — the capital is IDLE/undeployed. This is NOT "underfunded": the account is not short of cash.',
  STALE_ACCOUNT_CRITICAL: 'STALE ACCOUNT (critical): no trades for an extended period (idle past the critical threshold). Verify the worker + signal feed. Advisory only.',
  STALE_ACCOUNT_WARNING: 'STALE ACCOUNT (warning): trade inactivity approaching the critical threshold.',
  STALE_ACCOUNT_EMERGENCY: 'STALE ACCOUNT (emergency): extreme trade inactivity.',
  REVIEW_OVERDUE: 'REVIEW OVERDUE: the supervisor has not reviewed this account within the review window.',
  RISK_REVIEW_REQUIRED: 'RISK REVIEW REQUIRED: a real daily-loss threshold was breached; review the strategy.',
  UNMANAGED_POSITIONS: 'UNMANAGED POSITIONS: open positions exist without an active managing worker.',
  LIVE_TRADING_BLOCKED: 'LIVE TRADING BLOCKED: live order routing is disabled. These are paper/simulation accounts and the platform is in safe mode — Adversyn never places live orders.',
  SAFE_MODE: 'SAFE MODE: the platform executes paper/simulation only — no live broker mutations or live orders.',
};

export function tooltipFor(code: string | null | undefined): string {
  if (!code) return '';
  const k = String(code).toUpperCase().replace(/\s+/g, '_');
  return STATUS_GLOSSARY[k] || '';
}
